import type { DeskAgent, DeskDetail } from '@deskworks/shared-types';
import { Link } from '@tanstack/react-router';
import { AlertTriangle, Bot, Crown, MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { AgentAvatar, desklingMood, type DesklingMood } from '@/components/agent-avatar';
import { Section } from '@/components/section';
import { EmptyState } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/lib/api';
import { useAgents, useApiMutation, useRuntimeProfiles } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { canLead } from '@/components/lead-agent-picker';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

export function DeskAgentsSection({ desk }: { desk: DeskDetail }) {
  const agents = useAgents(desk.workspaceId);
  const profiles = useRuntimeProfiles(desk.workspaceId);
  const [pick, setPick] = useState('');
  const invalidate = [qk.desk(desk.id), qk.desks(desk.workspaceId), qk.agentAll];

  const setAgent = useApiMutation(
    (v: { agentId: string; role: 'lead' | 'available'; enabled?: boolean }) =>
      api.desks.setAgent(desk.id, v.agentId, { role: v.role, enabled: v.enabled }),
    { invalidate },
  );
  const removeAgent = useApiMutation((agentId: string) => api.desks.removeAgent(desk.id, agentId), {
    invalidate,
    success: t('Agent removed from desk'),
  });

  const onDesk = new Set(desk.agents.map((a) => a.agentId));
  const addable = (agents.data ?? []).filter((a) => !onDesk.has(a.id));
  const sorted = [...desk.agents].sort((a, b) => (a.role === b.role ? a.name.localeCompare(b.name) : a.role === 'lead' ? -1 : 1));
  const hasLead = desk.agents.some((a) => a.role === 'lead');

  return (
    <Section
      icon={<Bot />}
      title={t('Agents')}
      description={t('Your Desklings: the Lead Agent (with the crown) hands out the work, available Agents take on Work Items. Asleep, at work or waving for you.')}
    >
      {!hasLead && (
        <div className="mb-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-[13px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          {t('Without a Lead Agent, the desk can’t decide on its own what to do. Choose one.')}
        </div>
      )}
      {sorted.length === 0 ? (
        <EmptyState compact title={t('No Agents yet')} description={t('Add an Agent below.')} />
      ) : (
        <ul className="-mx-5 divide-y border-y">
          {sorted.map((a) => (
            <AgentRow
              key={a.agentId}
              agent={a}
              mood={desklingMood(desk.pulse, a.agentId)}
              workspaceId={desk.workspaceId}
              busy={setAgent.isPending || removeAgent.isPending}
              canLead={(() => {
                const full = agents.data?.find((x) => x.id === a.agentId);
                return !full || canLead(full, profiles.data);
              })()}
              onMakeLead={() => setAgent.mutate({ agentId: a.agentId, role: 'lead', enabled: true })}
              onToggle={() => setAgent.mutate({ agentId: a.agentId, role: a.role, enabled: !a.enabled })}
              onRemove={() => removeAgent.mutate(a.agentId)}
            />
          ))}
        </ul>
      )}
      <div className="mt-3 flex gap-2">
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger size="sm" className="min-w-0 flex-1" disabled={addable.length === 0}>
            <SelectValue placeholder={addable.length ? t('Add an Agent …') : t('All Agents are already on this desk')} />
          </SelectTrigger>
          <SelectContent>
            {addable.map((a) => {
              // Without a lead, the added agent becomes the lead — it needs an active runtime.
              const disabled = !hasLead && !canLead(a, profiles.data);
              return (
                <SelectItem key={a.id} value={a.id} disabled={disabled}>
                  {a.name}
                  {disabled && <span className="ml-1.5 text-xs text-muted-foreground">· {t('no runtime')}</span>}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          disabled={!pick}
          loading={setAgent.isPending}
          onClick={() =>
            setAgent.mutate(
              { agentId: pick, role: hasLead ? 'available' : 'lead', enabled: true },
              { onSuccess: () => setPick('') },
            )
          }
        >
          <Plus /> {hasLead ? t('Add') : t('As Lead')}
        </Button>
      </div>
    </Section>
  );
}

function AgentRow({
  agent,
  mood,
  workspaceId,
  busy,
  onMakeLead,
  onToggle,
  onRemove,
  canLead: leadable,
}: {
  mood?: DesklingMood;
  agent: DeskAgent;
  workspaceId: string;
  busy: boolean;
  /** False when the agent has no active runtime (the backend rejects it as lead). */
  canLead: boolean;
  onMakeLead: () => void;
  onToggle: () => void;
  onRemove: () => void;
}) {
  return (
    <li className={cn('flex items-center gap-3 px-5 py-2.5', !agent.enabled && 'opacity-60')}>
      <AgentAvatar seed={agent.agentId} size={36} lead={agent.role === 'lead'} muted={!agent.enabled} mood={mood} />
      <div className="min-w-0 flex-1">
        <Link
          to="/w/$workspaceId/agents/$agentId"
          params={{ workspaceId, agentId: agent.agentId }}
          className="truncate text-sm font-medium hover:text-primary hover:underline"
        >
          {agent.name}
        </Link>
        <div className="mt-0.5 flex items-center gap-1.5">
          {agent.role === 'lead' ? <Badge tone="accent">{t('Lead Agent')}</Badge> : <Badge tone="outline">{t('Available')}</Badge>}
          {!agent.enabled && <Badge tone="muted">{t('Disabled')}</Badge>}
        </div>
      </div>
      <Switch checked={agent.enabled} onCheckedChange={onToggle} disabled={busy} aria-label={t('Active on this desk')} title={t('Active on this desk')} />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon-sm" aria-label={t('More actions')}>
            <MoreHorizontal />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {agent.role !== 'lead' && (
            <DropdownMenuItem onSelect={onMakeLead} disabled={!leadable}>
              <Crown /> {leadable ? t('Make Lead Agent') : t('Make Lead Agent (needs a runtime)')}
            </DropdownMenuItem>
          )}
          {agent.role !== 'lead' && <DropdownMenuSeparator />}
          <DropdownMenuItem variant="destructive" onSelect={onRemove}>
            <Trash2 /> {t('Remove from desk')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </li>
  );
}
